import React, { useState, useContext, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from 'components/user/AuthContext';
import { NavigationUtils } from 'lib/navigationUtilities';
import { MessageLoading } from 'components/element/MessageTypes';
import Button from 'components/element/Button';
import Input from 'components/element/Input';
import Toast from 'components/element/Toast';
import BackTitle from 'components/element/BackTitle';
import JoinedDate from 'components/layout/JoinedDate';
import Banner from './Banner';
import api from 'lib/api';

const EditProfile = () => {
    const { user, refreshUser, isLoggedIn, isLoading } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const { navigateToLocation } = NavigationUtils();

    const [formData, setFormData] = useState({
        name: '',
        bio: '',
        avatar: null,
        banner: null
    });

    const [previews, setPreviews] = useState({
        avatar: null,
        banner: null
    });

    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (!isLoading && !isLoggedIn) {
            navigateToLocation('/login', { state: { from: '/profile/edit' } });
        }
    }, [isLoggedIn, isLoading, navigateToLocation]);

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                bio: user.bio || '',
                avatar: null,
                banner: null
            });
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            const nameChanged = formData.name !== user.name;
            const bioChanged = formData.bio !== user.bio;
            const avatarChanged = formData.avatar !== null;
            const bannerChanged = formData.banner !== null;

            setHasChanges(nameChanged || bioChanged || avatarChanged || bannerChanged);
        }
    }, [formData, user]);

    const updateProfileMutation = useMutation({
        mutationFn: () => {
            const formPayload = new FormData();

            formPayload.append('_method', 'PUT');
            formPayload.append('name', formData.name);
            formPayload.append('bio', formData.bio);

            if (formData.avatar instanceof File) {
                formPayload.append('avatar', formData.avatar);
            }

            if (formData.banner instanceof File) {
                formPayload.append('banner', formData.banner);
            }

            return api.auth.updateProfile(formPayload);
        },
        onSuccess: (data) => {
            const updatedUser = {
                ...user,
                name: data.user.name,
                bio: data.user.bio,
                avatar: previews.avatar || (formData.avatar ? URL.createObjectURL(formData.avatar) : user.avatar),
                banner: previews.banner || (formData.banner ? URL.createObjectURL(formData.banner) : user.banner)
            };

            refreshUser(updatedUser);

            queryClient.removeQueries({
                predicate: (query) =>
                    query.queryKey[0] === 'twutsList' ||
                    query.queryKey[0] === 'twuts'
            });

            setToast({
                show: true,
                message: 'Profile updated successfully',
                type: 'success'
            });

            setFormData(prev => ({
                ...prev,
                avatar: null,
                banner: null
            }));

            setHasChanges(false);
        },
        onError: (error) => {
            setHasChanges(true);
            console.log(error);
            setToast({
                show: true,
                message: error.response?.data?.message || 'Failed to update profile',
                type: 'danger'
            });
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e, field, maxSize, maxSizeText) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.match('image.*')) {
            setToast({
                show: true,
                message: 'Please select an image file',
                type: 'danger'
            });
            return;
        }

        if (file.size > maxSize) {
            setToast({
                show: true,
                message: `Image must be less than ${maxSizeText}`,
                type: 'danger'
            });
            return;
        }

        if (previews[field]) {
            URL.revokeObjectURL(previews[field]);
        }

        setFormData(prev => ({
            ...prev,
            [field]: file
        }));

        const objectUrl = URL.createObjectURL(file);
        setPreviews(prev => ({
            ...prev,
            [field]: objectUrl
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateProfileMutation.mutate();
    };

    const handleCancel = () => {
        Object.values(previews).forEach(url => {
            if (url) URL.revokeObjectURL(url);
        });

        setFormData({
            name: user.name || '',
            bio: user.bio || '',
            avatar: null,
            banner: null
        });

        setPreviews({
            avatar: null,
            banner: null
        });

        setHasChanges(false);

        if (document.getElementById('avatar-upload')) {
            document.getElementById('avatar-upload').value = '';
        }
        if (document.getElementById('banner-upload')) {
            document.getElementById('banner-upload').value = '';
        }
    };

    if (isLoading) return <MessageLoading message="Loading Profile..." />;
    if (!isLoggedIn || !user) return null;

    return (
        <div className="profile-edit">
            {toast.show && (<Toast message={toast.message} type={toast.type} onClose={() => setToast(prev => ({ ...prev, show: false }))} />)}

            <div className="border-t border-b border-gray-less mb-3">
                <BackTitle onClick={() => navigateToLocation(`/${user.username}`)} hoverText="Back to Profile" title="Edit Profile" subtitle={`@${user.username}`} />

                <div className="relative">
                    <div className="absolute inset-0 h-40 z-10">
                        <label htmlFor="banner-upload" className="block w-full h-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                            <div className="text-white text-center">
                                <i className="fa-solid fa-image text-2xl"></i>
                                <p className="text-sm mt-1">Change Banner</p>
                            </div>
                        </label>
                    </div>
                    <input
                        type="file"
                        id="banner-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'banner', 4 * 1024 * 1024, '4MB')}
                    />

                    <Banner
                        avatar={previews.avatar || user?.avatar}
                        banner={previews.banner || user?.banner}
                        avatarRawSrc={!!previews.avatar}
                        bannerRawSrc={!!previews.banner}
                        username={user?.username}
                    >
                        <label htmlFor="avatar-upload" className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer z-20">
                            <div className="text-white text-center">
                                <i className="fa-solid fa-camera text-2xl"></i>
                                <p className="text-sm mt-1">Change Avatar</p>
                            </div>
                        </label>
                        <input
                            type="file"
                            id="avatar-upload"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, 'avatar', 2 * 1024 * 1024, '2MB')}
                        />
                    </Banner>
                </div>
            </div>

            <div className="mt-16 px-4">
                <JoinedDate date={user.created_at} />
            </div>

            <form onSubmit={handleSubmit} className="mt-6 px-4">
                <div className="mb-4">
                    <label className="block text-gray-stronger mb-2" htmlFor="name">Display Name</label>
                    <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your display name"
                        maxLength={50}
                        required
                        className="w-full"
                    />
                </div>

                <div className="mb-6">
                    <label className="block text-gray-stronger mb-2" htmlFor="bio">Bio</label>
                    <Input
                        id="bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleChange}
                        placeholder="Write a short bio about yourself"
                        rows={3}
                        maxLength={500}
                        className="w-full"
                    />
                </div>

                {hasChanges && (
                    <div className="flex space-x-4 mb-8">
                        <Button type="submit" className="w-40 text-white bg-blue-mid hover:bg-blue-more" isLoading={updateProfileMutation.isPending} disabled={updateProfileMutation.isPending}>
                            Save Changes
                        </Button>
                        <Button type="button" onClick={handleCancel} className="w-40 text-white bg-gray-mid hover:bg-gray-more" disabled={updateProfileMutation.isPending}>
                            Cancel Changes
                        </Button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default EditProfile;
